import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HiPlus, HiX, HiArrowLeft } from 'react-icons/hi';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import { supabase } from '../supabase/client';
import { useAuth } from '../context/AuthContext';
import { formatAmount, formatDate, nextInvoiceNumber } from '../utils/format';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EMPTY_ITEM = { description: '', quantity: 1, price: '' };

function inputCls(hasError) {
  return [
    'w-full px-3 py-2.5 border rounded-xl text-sm text-gray-900 placeholder-gray-400',
    'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition',
    hasError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300',
  ].join(' ');
}

function SectionCard({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      {title && <h3 className="text-sm font-semibold text-gray-900 mb-5">{title}</h3>}
      {children}
    </div>
  );
}

function FormLabel({ children, required }) {
  return (
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
      {children}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function InvoiceCreate() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const { id }     = useParams();        // present only on /invoices/:id/edit
  const isEdit     = !!id;

  const [clients,        setClients]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);

  // Form state
  const [clientId,       setClientId]       = useState('');
  const [invoiceNumber,  setInvoiceNumber]  = useState('');
  const [dueDate,        setDueDate]        = useState('');
  const [notes,          setNotes]          = useState('');
  const [items,          setItems]          = useState([{ ...EMPTY_ITEM }]);
  const [errors,         setErrors]         = useState({});

  // ── Load clients + (optionally) existing invoice ───────────────────────────

  useEffect(() => {
    const init = async () => {
      setLoading(true);

      // Always load active clients for dropdown
      const { data: clientData } = await supabase
        .from('clients')
        .select('id, name')
        .eq('user_id', user.id)
        .eq('status', 'Active')
        .order('name');
      setClients(clientData ?? []);

      if (isEdit) {
        const { data: inv, error } = await supabase
          .from('invoices')
          .select('*')
          .eq('id', id)
          .eq('user_id', user.id)
          .single();

        if (error || !inv) {
          toast.error('Invoice not found');
          navigate('/invoices');
          return;
        }
        setClientId(inv.client_id ?? '');
        setInvoiceNumber(inv.invoice_number ?? '');
        setDueDate(inv.due_date ?? '');
        setNotes(inv.notes ?? '');
        setItems(inv.items?.length ? inv.items : [{ ...EMPTY_ITEM }]);
      } else {
        // Auto-generate next invoice number
        const { data: last } = await supabase
          .from('invoices')
          .select('invoice_number')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);
        setInvoiceNumber(nextInvoiceNumber(last));
      }

      setLoading(false);
    };

    init();
  }, [user.id, id, isEdit, navigate]);

  // ── Line item helpers ──────────────────────────────────────────────────────

  const setItem = (idx, field, val) =>
    setItems((prev) => prev.map((item, i) => i === idx ? { ...item, [field]: val } : item));

  const addItem    = () => setItems((prev) => [...prev, { ...EMPTY_ITEM }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const total = items.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.price) || 0),
    0
  );

  // ── Validation ─────────────────────────────────────────────────────────────

  const validate = () => {
    const errs = {};
    if (!clientId)       errs.clientId      = 'Select a client';
    if (!invoiceNumber)  errs.invoiceNumber  = 'Invoice number required';
    if (!dueDate)        errs.dueDate        = 'Due date required';
    const hasValidItem = items.some((i) => i.description.trim() && Number(i.price) > 0);
    if (!hasValidItem)   errs.items          = 'Add at least one item with a description and price';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const save = async (status) => {
    if (!validate()) return;
    setSaving(true);

    const payload = {
      user_id:        user.id,
      client_id:      clientId,
      invoice_number: invoiceNumber,
      status,
      amount:         total,
      due_date:       dueDate,
      items:          items.filter((i) => i.description.trim()),
      notes:          notes.trim() || null,
    };

    const { error } = isEdit
      ? await supabase.from('invoices').update(payload).eq('id', id).eq('user_id', user.id)
      : await supabase.from('invoices').insert([payload]);

    if (error) {
      toast.error(error.message);
    } else {
      const msg = isEdit ? 'Invoice updated!' : status === 'Draft' ? 'Draft saved!' : 'Invoice created!';
      toast.success(msg);
      navigate('/invoices');
    }
    setSaving(false);
  };

  // ── Loading spinner ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout title={isEdit ? 'Edit Invoice' : 'New Invoice'}>
        <div className="flex items-center justify-center py-32">
          <svg className="animate-spin h-8 w-8 text-indigo-500" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      </Layout>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout title={isEdit ? 'Edit Invoice' : 'New Invoice'}>

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/invoices')}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition"
        >
          <HiArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? 'Edit Invoice' : 'New Invoice'}
          </h2>
          <p className="text-sm text-gray-400 mt-0.5">{invoiceNumber}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">

        {/* ── Left / main column ── */}
        <div className="xl:col-span-2 space-y-5">

          {/* Invoice details */}
          <SectionCard title="Invoice Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

              {/* Client — full width */}
              <div className="sm:col-span-2">
                <FormLabel required>Client</FormLabel>
                <select
                  value={clientId}
                  onChange={(e) => { setClientId(e.target.value); setErrors((p) => ({ ...p, clientId: '' })); }}
                  className={inputCls(errors.clientId)}
                >
                  <option value="">— Select a client —</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {errors.clientId && <p className="mt-1.5 text-xs text-red-500">{errors.clientId}</p>}
                {clients.length === 0 && (
                  <p className="mt-1.5 text-xs text-amber-600">
                    No active clients yet —{' '}
                    <button onClick={() => navigate('/clients')} className="underline font-medium">
                      add one first
                    </button>
                  </p>
                )}
              </div>

              {/* Invoice number */}
              <div>
                <FormLabel required>Invoice Number</FormLabel>
                <input
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => { setInvoiceNumber(e.target.value); setErrors((p) => ({ ...p, invoiceNumber: '' })); }}
                  className={inputCls(errors.invoiceNumber)}
                />
                {errors.invoiceNumber && <p className="mt-1.5 text-xs text-red-500">{errors.invoiceNumber}</p>}
              </div>

              {/* Due date */}
              <div>
                <FormLabel required>Due Date</FormLabel>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => { setDueDate(e.target.value); setErrors((p) => ({ ...p, dueDate: '' })); }}
                  className={inputCls(errors.dueDate)}
                />
                {errors.dueDate && <p className="mt-1.5 text-xs text-red-500">{errors.dueDate}</p>}
              </div>
            </div>
          </SectionCard>

          {/* Line items */}
          <SectionCard title="Line Items">

            {/* Column header */}
            <div className="grid grid-cols-[minmax(0,1fr)_72px_96px_36px] gap-2 mb-2 px-0.5">
              {['Description', 'Qty', 'Price', ''].map((h) => (
                <p key={h} className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{h}</p>
              ))}
            </div>

            {/* Item rows */}
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-[minmax(0,1fr)_72px_96px_36px] gap-2 items-center">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => setItem(idx, 'description', e.target.value)}
                    placeholder="Service or product description"
                    className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm
                      text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2
                      focus:ring-indigo-500 focus:border-transparent"
                  />
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => setItem(idx, 'quantity', e.target.value)}
                    className="w-full px-2 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm
                      text-center text-gray-900 focus:outline-none focus:ring-2
                      focus:ring-indigo-500 focus:border-transparent"
                  />
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.price}
                      onChange={(e) => setItem(idx, 'price', e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-6 pr-2 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm
                        text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500
                        focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => removeItem(idx)}
                    disabled={items.length === 1}
                    title="Remove item"
                    className="p-1.5 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-lg
                      transition disabled:opacity-25 disabled:cursor-not-allowed"
                  >
                    <HiX className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {errors.items && <p className="mt-2 text-xs text-red-500">{errors.items}</p>}

            <button
              onClick={addItem}
              className="mt-4 flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition"
            >
              <HiPlus className="w-4 h-4" /> Add line item
            </button>

            {/* Subtotals */}
            <div className="mt-5 pt-4 border-t border-gray-100 space-y-1.5">
              {items
                .filter((i) => i.description.trim() && Number(i.price) > 0)
                .map((item, idx) => (
                  <div key={idx} className="flex justify-between text-xs text-gray-500">
                    <span className="truncate mr-4">{item.description} × {item.quantity}</span>
                    <span className="flex-shrink-0 font-medium">
                      {formatAmount(Number(item.quantity) * Number(item.price))}
                    </span>
                  </div>
                ))
              }
              <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                <span className="text-base font-bold text-gray-900">Total</span>
                <span className="text-lg font-bold text-indigo-600">{formatAmount(total)}</span>
              </div>
            </div>
          </SectionCard>

          {/* Notes */}
          <SectionCard>
            <FormLabel>Notes (optional)</FormLabel>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Payment terms, bank details, thank you message…"
              className="w-full px-3 py-2.5 border border-gray-200 bg-gray-50 rounded-xl text-sm
                text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2
                focus:ring-indigo-500 focus:border-transparent resize-none"
            />
          </SectionCard>
        </div>

        {/* ── Right / summary sidebar ── */}
        <div className="space-y-4 xl:sticky xl:top-6">

          {/* Summary card */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Client</span>
                <span className="font-medium text-gray-900 truncate ml-4 text-right">
                  {clients.find((c) => c.id === clientId)?.name ?? '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Invoice #</span>
                <span className="font-medium text-gray-900">{invoiceNumber || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Due date</span>
                <span className="font-medium text-gray-900">{dueDate ? formatDate(dueDate) : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Line items</span>
                <span className="font-medium text-gray-900">
                  {items.filter((i) => i.description.trim()).length}
                </span>
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                <span className="font-bold text-gray-900">Total</span>
                <span className="text-xl font-bold text-indigo-600">{formatAmount(total)}</span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-2.5">
            <button
              onClick={() => save('Pending')}
              disabled={saving}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3
                rounded-xl transition shadow-sm disabled:opacity-60 text-sm"
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Saving…
                </span>
              ) : isEdit ? 'Save Changes' : 'Save & Send'}
            </button>
            {!isEdit && (
              <button
                onClick={() => save('Draft')}
                disabled={saving}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3
                  rounded-xl transition text-sm disabled:opacity-60"
              >
                Save as Draft
              </button>
            )}
            <button
              onClick={() => navigate('/invoices')}
              className="w-full text-gray-400 hover:text-gray-600 font-medium py-2 text-sm transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
